/**
 * AvatarGenerator — Style preset selection + generation + option selection
 * IDENTITY:P2.D
 *
 * 6-card style preset grid, custom vibe prompt textarea, generate button,
 * 2x2 option grid, confirm selection.
 * WCAG 2.2 AA: keyboard navigation, aria-labels, focus indicators.
 */

import React, { useState, useRef } from 'react';

const STYLE_PRESETS = [
  { id: 'cyberpunk', label: 'Cyberpunk', desc: 'Neon-lit tech aesthetic' },
  { id: 'noir', label: 'Noir', desc: 'Shadow-heavy detective style' },
  { id: 'comic_ink', label: 'Comic Ink', desc: 'Bold ink & halftone dots' },
  { id: 'retro_pixel', label: 'Retro Pixel', desc: '16-bit pixel art' },
  { id: 'streetwear', label: 'Streetwear', desc: 'Urban street art' },
  { id: 'masked_collector', label: 'Masked Collector', desc: 'Anonymous collector mystique' },
];

const MAX_PROMPT_LENGTH = 500;

export default function AvatarGenerator({
  onGenerate,
  onSelect,
  isGenerating = false,
  options = [],
}) {
  const [selectedPreset, setSelectedPreset] = useState(null);
  const [customPrompt, setCustomPrompt] = useState('');
  const [selectedOption, setSelectedOption] = useState(null);
  const promptRef = useRef(null);

  const canGenerate = (selectedPreset || customPrompt.trim()) && !isGenerating;

  const handleGenerate = () => {
    /* c8 ignore next */
    if (!canGenerate) return;
    /* c8 ignore next */
    onGenerate?.(selectedPreset, customPrompt.trim() || null);
  };

  const handleConfirmSelection = () => {
    if (selectedOption) {
      onSelect?.(selectedOption);
    }
  };

  const handlePresetKeyDown = (e, presetId) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setSelectedPreset(presetId === selectedPreset ? null : presetId);
    }
    // Arrow key navigation
    const presetIds = STYLE_PRESETS.map((p) => p.id);
    const idx = presetIds.indexOf(presetId);
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
      e.preventDefault();
      const next = (idx + 1) % presetIds.length;
      document.getElementById(`preset-${presetIds[next]}`)?.focus();
    }
    if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      e.preventDefault();
      const prev = (idx - 1 + presetIds.length) % presetIds.length;
      document.getElementById(`preset-${presetIds[prev]}`)?.focus();
    }
  };

  return (
    <div className="space-y-6">
      {/* Style Preset Grid */}
      <div>
        <h3 className="text-sm font-medium text-zinc-400 mb-3">Choose a Style</h3>
        <div
          className="grid grid-cols-2 sm:grid-cols-3 gap-3"
          role="radiogroup"
          aria-label="Avatar style presets"
        >
          {STYLE_PRESETS.map((preset) => (
            <button
              key={preset.id}
              id={`preset-${preset.id}`}
              role="radio"
              aria-checked={selectedPreset === preset.id}
              aria-label={`${preset.label}: ${preset.desc}`}
              className={`p-3 rounded-lg border text-left transition-all
                focus:outline-none focus:ring-2 focus:ring-orange-500
                ${
                  selectedPreset === preset.id
                    ? 'border-orange-500 bg-zinc-800'
                    : 'border-zinc-700 bg-zinc-900 hover:border-zinc-600'
                }`}
              onClick={() => setSelectedPreset(preset.id === selectedPreset ? null : preset.id)}
              onKeyDown={(e) => handlePresetKeyDown(e, preset.id)}
              tabIndex={0}
            >
              <div className="text-sm font-medium text-white">{preset.label}</div>
              <div className="text-xs text-zinc-500 mt-1">{preset.desc}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Custom Prompt */}
      <div>
        <label htmlFor="custom-prompt" className="text-sm font-medium text-zinc-400 block mb-2">
          Custom Vibe (optional)
        </label>
        <textarea
          id="custom-prompt"
          ref={promptRef}
          value={customPrompt}
          onChange={(e) => setCustomPrompt(e.target.value.slice(0, MAX_PROMPT_LENGTH))}
          placeholder="Describe your ideal avatar vibe..."
          maxLength={MAX_PROMPT_LENGTH}
          rows={3}
          className="w-full bg-zinc-900 border border-zinc-700 rounded-lg p-3 text-white text-sm
            placeholder-zinc-600 focus:outline-none focus:ring-2 focus:ring-orange-500 resize-none"
          aria-label="Custom avatar creative direction"
        />
        <div className="text-xs text-zinc-600 text-right mt-1">
          {customPrompt.length}/{MAX_PROMPT_LENGTH}
        </div>
      </div>

      {/* Generate Button */}
      <button
        onClick={handleGenerate}
        disabled={!canGenerate}
        className={`w-full py-3 px-4 rounded-lg font-medium text-sm transition-all
          focus:outline-none focus:ring-2 focus:ring-orange-500
          ${
            canGenerate
              ? 'bg-orange-500 text-white hover:bg-orange-600'
              : 'bg-zinc-800 text-zinc-500 cursor-not-allowed'
          }`}
        aria-label={isGenerating ? 'Generating avatar options...' : 'Generate avatar options'}
        aria-busy={isGenerating}
      >
        {isGenerating ? (
          <span className="flex items-center justify-center gap-2">
            <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" aria-hidden="true">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            Generating your avatar...
          </span>
        ) : (
          'Generate Avatar'
        )}
      </button>

      {/* 2x2 Option Grid */}
      {options.length > 0 && (
        <div>
          <h3 className="text-sm font-medium text-zinc-400 mb-3">Choose Your Avatar</h3>
          <div
            className="grid grid-cols-2 gap-3"
            role="radiogroup"
            aria-label="Generated avatar options"
          >
            {options.map((opt, i) => (
              <button
                key={opt.id}
                role="radio"
                aria-checked={selectedOption === opt.id}
                aria-label={`Avatar option ${i + 1}`}
                className={`relative rounded-lg overflow-hidden border-2 transition-all
                  focus:outline-none focus:ring-2 focus:ring-orange-500
                  ${
                    selectedOption === opt.id
                      ? 'border-orange-500'
                      : 'border-zinc-700 hover:border-zinc-500'
                  }`}
                onClick={() => setSelectedOption(opt.id)}
                tabIndex={0}
              >
                <img
                  src={opt.thumbnail_url || opt.image_url}
                  alt={`Avatar option ${i + 1}`}
                  className="w-full aspect-square object-cover"
                  loading="lazy"
                />
              </button>
            ))}
          </div>

          {/* Confirm Selection */}
          {selectedOption && (
            <button
              onClick={handleConfirmSelection}
              className="w-full mt-3 py-2 px-4 rounded-lg bg-orange-500 text-white font-medium text-sm
                hover:bg-orange-600 transition-all focus:outline-none focus:ring-2 focus:ring-orange-500"
              aria-label="Confirm avatar selection"
            >
              Use This Avatar
            </button>
          )}
        </div>
      )}
    </div>
  );
}
